import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, Calendar, Gauge, Palette, Eye, Truck, X, ChevronLeft, ChevronRight, ZoomIn, Share2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { API, photoUrl } from '../lib/api';

const VehicleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const touchStartX = useRef(null);

  useEffect(() => {
    fetchVehicle();
    // Increment view counter
    if (id) {
      axios.post(`${API}/vehicles/${id}/view`).catch(err =>
        console.error('Failed to increment view:', err)
      );
    }
  }, [id]);

  const fetchVehicle = async () => {
    try {
      const res = await axios.get(`${API}/vehicles/${id}`);
      setVehicle(res.data);
    } catch (error) {
      console.error('Error fetching vehicle:', error);
      toast.error('Erreur lors du chargement du véhicule');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce véhicule ?')) return;

    try {
      await axios.delete(`${API}/vehicles/${id}`);
      toast.success('Véhicule supprimé avec succès');
      navigate('/vehicles');
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const buildPublicShareUrl = () => {
    const base = process.env.REACT_APP_PUBLIC_URL || window.location.origin;
    return `${base}/catalogue?v=${id}`;
  };

  const handleShare = async () => {
    const url = buildPublicShareUrl();
    const title = `${vehicle.marque} ${vehicle.modele}`;
    const text = `Découvrez ${vehicle.marque} ${vehicle.modele} (${vehicle.annee}) - Groupe Auto Prestige`;
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (err) {
        if (err && err.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Lien de la fiche copié !');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Impossible de copier le lien');
    }
  };

  const totalPhotos = vehicle?.photos?.length || 0;

  const prevPhoto = useCallback(() => {
    if (totalPhotos < 2) return;
    setSelectedImage(prev => (prev - 1 + totalPhotos) % totalPhotos);
  }, [totalPhotos]);

  const nextPhoto = useCallback(() => {
    if (totalPhotos < 2) return;
    setSelectedImage(prev => (prev + 1) % totalPhotos);
  }, [totalPhotos]);

  // Navigation clavier dans la lightbox
  useEffect(() => {
    if (!lightboxOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') setLightboxOpen(false);
      else if (e.key === 'ArrowLeft') prevPhoto();
      else if (e.key === 'ArrowRight') nextPhoto();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxOpen, prevPhoto, nextPhoto]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) {
      if (dx > 0) prevPhoto();
      else nextPhoto();
    }
    touchStartX.current = null;
  };

  if (loading) {
    return <div className="text-center py-12">Chargement...</div>;
  }

  if (!vehicle) {
    return <div className="text-center py-12">Véhicule non trouvé</div>;
  }

  return (
    <div data-testid="vehicle-detail-page">
      <div className="mb-6">
        <Link to="/vehicles" data-testid="back-to-vehicles-btn" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
          <ArrowLeft size={20} />
          <span>Retour aux véhicules</span>
        </Link>
      </div>

      <div className="flex justify-between items-start mb-8">
        <div>
          <h2 className="text-3xl font-bold tracking-tight text-slate-900" style={{fontFamily: 'Manrope'}}>
            {vehicle.marque} {vehicle.modele}
          </h2>
          <p className="text-slate-600 font-mono text-lg mt-1">{vehicle.immatriculation}</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleShare}
            data-testid="share-vehicle-btn"
            title="Partager le lien de la fiche publique du véhicule"
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center gap-2"
          >
            {copied ? <Check size={18} /> : <Share2 size={18} />}
            {copied ? 'Copié !' : 'Partager'}
          </button>
          <Link
            to={`/vehicles/${id}/edit`}
            data-testid="edit-vehicle-btn"
            className="px-4 py-2 bg-white border border-slate-200 text-slate-900 rounded-md hover:bg-slate-50 transition-colors flex items-center gap-2"
          >
            <Edit size={18} />
            Modifier
          </Link>
          <button
            onClick={handleDelete}
            data-testid="delete-vehicle-btn"
            className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors flex items-center gap-2"
          >
            <Trash2 size={18} />
            Supprimer
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {vehicle.photos && vehicle.photos.length > 0 ? (
            <div className="space-y-4">
              <div
                className="relative bg-slate-200 rounded-lg overflow-hidden group cursor-zoom-in"
                onClick={() => setLightboxOpen(true)}
                data-testid="main-image-wrapper"
              >
                <img
                  src={photoUrl(vehicle.photos[selectedImage])}
                  alt="Vehicle"
                  className="w-full h-96 object-cover pointer-events-none"
                  data-testid="main-vehicle-image"
                />
                {/* Overlay zoom indicator */}
                <div className="pointer-events-none absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 text-slate-900 px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-2 shadow-lg">
                    <ZoomIn size={16} />
                    Cliquer pour agrandir
                  </div>
                </div>
                {/* Compteur photos */}
                {vehicle.photos.length > 1 && (
                  <div className="pointer-events-none absolute bottom-3 right-3 bg-black/60 text-white text-xs font-medium px-2.5 py-1 rounded-full">
                    {selectedImage + 1} / {vehicle.photos.length}
                  </div>
                )}
              </div>
              {vehicle.photos.length > 1 && (
                <div className="grid grid-cols-4 gap-4">
                  {vehicle.photos.map((photo, index) => (
                    <div
                      key={index}
                      data-testid={`thumbnail-${index}`}
                      onClick={() => setSelectedImage(index)}
                      className={`cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImage === index ? 'border-slate-900 scale-105' : 'border-slate-200 hover:border-slate-400'
                      }`}
                    >
                      <img src={photoUrl(photo)} alt={`Thumbnail ${index + 1}`} className="gallery-image h-24" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-slate-200 rounded-lg h-96 flex items-center justify-center text-slate-400">
              Aucune photo disponible
            </div>
          )}

          {vehicle.description && (
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 mt-6">
              <h3 className="text-xl font-semibold text-slate-900 mb-3" style={{fontFamily: 'Manrope'}}>Description</h3>
              <p className="text-slate-600 leading-relaxed">{vehicle.description}</p>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>Statut</h3>
              <span className={`status-badge status-${vehicle.statut}`} data-testid="vehicle-status">
                {vehicle.statut === 'en_stock' ? 'En stock' : vehicle.statut === 'vendu' ? 'Vendu' : 'Réservé'}
              </span>
            </div>
            {vehicle.views > 0 && (
              <div className="flex items-center gap-2 text-slate-600 mt-3 pt-3 border-t border-slate-200">
                <Eye size={18} className="text-blue-500" />
                <div>
                  <p className="text-xs text-slate-500">Nombre de vues</p>
                  <p className="font-semibold text-slate-900" data-testid="vehicle-views">{vehicle.views}</p>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
            <h3 className="text-xl font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>Informations</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-slate-600">
                <Calendar size={18} className="text-slate-400" />
                <div className="flex-1">
                  <p className="text-xs text-slate-500">Année</p>
                  <p className="font-mono text-slate-900">{vehicle.annee}</p>
                </div>
              </div>
              
              {vehicle.type_vehicule && (
                <div className="flex items-center gap-3 text-slate-600">
                  <Truck size={18} className="text-slate-400" />
                  <div className="flex-1">
                    <p className="text-xs text-slate-500">Type</p>
                    <p className="text-slate-900 capitalize">{vehicle.type_vehicule === 'utilitaire' ? 'Utilitaire' : 'Voiture'}</p>
                  </div>
                </div>
              )}
              
              {vehicle.couleur && (
                <div className="flex items-center gap-3 text-slate-600">
                  <Palette size={18} className="text-slate-400" />
                  <div className="flex-1">
                    <p className="text-xs text-slate-500">Couleur</p>
                    <p className="text-slate-900">{vehicle.couleur}</p>
                  </div>
                </div>
              )}
              
              {vehicle.kilometrage && (
                <div className="flex items-center gap-3 text-slate-600">
                  <Gauge size={18} className="text-slate-400" />
                  <div className="flex-1">
                    <p className="text-xs text-slate-500">Kilométrage</p>
                    <p className="font-mono text-slate-900">{vehicle.kilometrage.toLocaleString('fr-FR')} km</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
            <h3 className="text-xl font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>Financier</h3>
            <div className="space-y-3">
              {vehicle.prix_vente && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Prix de vente TTC</p>
                  <p className="text-2xl font-mono font-bold text-green-600" data-testid="sale-price">
                    {vehicle.prix_vente.toLocaleString('fr-FR')} €
                  </p>
                  {vehicle.tva_applicable && (
                    <p className="text-sm text-slate-600 font-mono mt-1" data-testid="sale-price-ht">
                      soit {Math.round(vehicle.prix_vente / 1.20).toLocaleString('fr-FR')} € HT
                      <span className="text-xs text-slate-400 ml-1">(TVA 20%)</span>
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
            <h3 className="text-xl font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>Achat</h3>
            <div className="space-y-2">
              <div>
                <p className="text-xs text-slate-500">Société</p>
                <p className="text-slate-900 font-medium">{vehicle.societe_nom}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Date d&apos;ajout</p>
                <p className="text-slate-900 font-mono text-sm">
                  {new Date(vehicle.date_ajout).toLocaleDateString('fr-FR')}
                </p>
              </div>
              {vehicle.date_vente && (
                <div>
                  <p className="text-xs text-slate-500">Date de vente</p>
                  <p className="text-slate-900 font-mono text-sm">
                    {new Date(vehicle.date_vente).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox plein écran */}
      {lightboxOpen && vehicle.photos && vehicle.photos.length > 0 && (
        <div
          className="fixed inset-0 bg-black/95 z-[60] flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
          data-testid="vehicle-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxOpen(false); }}
            data-testid="lightbox-close"
            className="absolute top-4 right-4 z-10 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
            aria-label="Fermer"
          >
            <X size={24} />
          </button>

          {vehicle.photos.length > 1 && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 bg-white/10 text-white text-sm font-medium px-4 py-2 rounded-full">
              {selectedImage + 1} / {vehicle.photos.length}
            </div>
          )}

          <div
            className="relative w-full h-full flex items-center justify-center p-4 sm:p-12"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <img
              src={photoUrl(vehicle.photos[selectedImage])}
              alt={`Photo ${selectedImage + 1}`}
              className="max-w-full max-h-full object-contain select-none"
              data-testid="lightbox-image"
              draggable={false}
            />
          </div>

          {vehicle.photos.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
                data-testid="lightbox-prev"
                className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 z-10 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
                aria-label="Photo précédente"
              >
                <ChevronLeft size={28} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
                data-testid="lightbox-next"
                className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 z-10 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
                aria-label="Photo suivante"
              >
                <ChevronRight size={28} />
              </button>
            </>
          )}

          {vehicle.photos.length > 1 && (
            <div
              className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2 px-3 py-2 bg-white/5 rounded-lg max-w-[90vw] overflow-x-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {vehicle.photos.map((photo, idx) => (
                <img
                  key={idx}
                  src={photoUrl(photo)}
                  alt={`Miniature ${idx + 1}`}
                  onClick={() => setSelectedImage(idx)}
                  className={`h-14 w-20 object-cover rounded cursor-pointer transition-all flex-shrink-0 ${
                    selectedImage === idx ? 'ring-2 ring-white opacity-100' : 'opacity-60 hover:opacity-100'
                  }`}
                />
              ))}
            </div>
          )}

          <div className="hidden sm:block absolute bottom-24 left-1/2 -translate-x-1/2 text-white/60 text-xs">
            ← → pour naviguer · Esc pour fermer
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleDetail;
